import React, { ChangeEvent, FormEvent, JSX, useCallback, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

import { regions as AllRegions } from '@luxuryescapes/lib-regions/lib/regions';

import PlaceLookUp from '~/components/Common/Forms/PlaceLookUp';
import Image from '~/components/Common/Image';
import ExistingTrendingPlaceImages from '~/components/Content/TrendingPlaces/ExistingTrendingPlaceImages';
import { TRENDING_PLACES_BRANDS } from '~/components/Content/TrendingPlaces/TrendingPlacesPage';
import { TYPEAHEAD_PLACE_TYPES } from '~/components/Content/TrendingPlaces/constants';

import { uploadImage } from '~/services/ImageService';
import SearchService, { TypeaheadSearchResult } from '~/services/SearchService';

type Props = {
  onClose: () => void;
};

const VisuallyHiddenInput = styled('input')({
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledButton = styled(IconButton)`
  margin-left: 10px;
`;

function UpdateTrendingPlaceImageForm({ onClose }: Props): JSX.Element {
  const [place, setPlace] = useState<TypeaheadSearchResult | null>(null);
  const [imageId, setImageId] = useState<string | null>(null);
  const [brands, setBrands] = useState<Array<string>>([]);
  const [regions, setRegions] = useState<Array<string>>([]);
  const allRegionCodes = useMemo(() => AllRegions['luxuryescapes'].map((region) => region.code), []);
  const allBrandValues = useMemo(() => TRENDING_PLACES_BRANDS.map((region) => region.value), []);
  const [error, setError] = useState<JSX.Element | null>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const handleSelectedPlace = useCallback((selection: TypeaheadSearchResult | undefined) => {
    setPlace(selection ?? null);
    setImageId(null);
  }, []);

  const onImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files[0];
    const res = await uploadImage(file);
    setImageId(res.public_id);
  };

  const handleBrandChange = (event: SelectChangeEvent<Array<string>>) => {
    event.preventDefault();
    const newBrands = event.target.value as Array<string>;
    const hasSelectAll = newBrands.includes('selectAll');
    const hadSelectAll = brands.includes('selectAll');
    const isAllSelected = newBrands.length === allBrandValues.length && !newBrands.includes('selectAll');

    if (hasSelectAll && !hadSelectAll) {
      setBrands(['selectAll', ...allBrandValues]);
    } else if (!hasSelectAll && hadSelectAll) {
      setBrands([]);
    } else if (hadSelectAll && hasSelectAll && !isAllSelected) {
      setBrands(newBrands.filter((brand) => brand !== 'selectAll'));
    } else if (isAllSelected && !hasSelectAll) {
      setBrands(['selectAll', ...allBrandValues]);
    } else {
      setBrands(newBrands);
    }
  };

  const handleRegionChange = (event: SelectChangeEvent<Array<string>>) => {
    event.preventDefault();
    const newRegions = event.target.value as Array<string>;
    const hasSelectAll = newRegions.includes('selectAll');
    const hadSelectAll = regions.includes('selectAll');
    const isAllSelected = newRegions.length === allRegionCodes.length && !newRegions.includes('selectAll');

    if (hasSelectAll && !hadSelectAll) {
      setRegions(['selectAll', ...allRegionCodes]);
    } else if (!hasSelectAll && hadSelectAll) {
      setRegions([]);
    } else if (hadSelectAll && hasSelectAll && !isAllSelected) {
      setRegions(newRegions.filter((region) => region !== 'selectAll'));
    } else if (isAllSelected && !hasSelectAll) {
      setRegions(['selectAll', ...allRegionCodes]);
    } else {
      setRegions(newRegions);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const failedToAdd: Array<{ brand: string; region: string }> = [];
    for (const region of regions.filter((region) => region !== 'selectAll')) {
      for (const brand of brands.filter((region) => region !== 'selectAll')) {
        try {
          const { status } = await SearchService.updatePlaceImage(place.id, brand, region, imageId);

          if (status !== 200) {
            failedToAdd.push({
              brand: TRENDING_PLACES_BRANDS.find((item) => item.value === brand).name,
              region: AllRegions['luxuryescapes'].find((item) => item.code === region).name,
            });
          }
        } catch (error) {
          failedToAdd.push({ brand, region });
        }
      }
    }

    if (failedToAdd.length > 0) {
      const errorMessage = (
        <Alert severity="error">
          <strong>Failed to update image to the following:</strong>
          <ul style={{ margin: '0', padding: '0', listStyle: 'none' }}>
            {failedToAdd.map((item, index) => (
              <li key={index}>
                <strong> - Brand:</strong> {item.brand}
                <strong> | Region:</strong> {item.region}
              </li>
            ))}
          </ul>
        </Alert>
      );

      setError(errorMessage);
      return;
    }

    setSuccessDialogOpen(true);
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    onClose();
  };

  return (
    <Container sx={{ mt: 3 }}>
      <form onSubmit={handleSubmit}>
        <Stack direction="column" gap={4}>
          <PlaceLookUp
            onCurrentValueChange={handleSelectedPlace}
            types={TYPEAHEAD_PLACE_TYPES}
            label="Destination whose image you want to update"
          />

          <ExistingTrendingPlaceImages placeId={place?.id} selectedImageId={imageId} onSelectImage={setImageId} />

          <Typography component="h3" variant="subtitle1">
            Apply the selected image to
          </Typography>

          <FormControl fullWidth variant="outlined">
            <InputLabel id="regions-select-label">Target Markets (Required)</InputLabel>
            <Select
              labelId="regions-select-label"
              multiple
              value={regions}
              onChange={handleRegionChange}
              renderValue={(selected: unknown) =>
                Array.isArray(selected) ? selected.filter((region) => region !== 'selectAll').join(', ') : ''
              }
              label="Target Markets (Required)"
              required
            >
              {[{ name: 'Select All', code: 'selectAll' }, ...AllRegions['luxuryescapes']].map((region) => (
                <MenuItem key={region.code} value={region.code}>
                  <Checkbox checked={regions.includes(region.code)} />
                  <ListItemText primary={region.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel id="brands-select-label">Target Brands (Required)</InputLabel>
            <Select
              labelId="brands-select-label"
              multiple
              value={brands}
              onChange={handleBrandChange}
              renderValue={(selected: unknown) =>
                Array.isArray(selected) ? selected.filter((brand: string) => brand !== 'selectAll').join(', ') : ''
              }
              label="Target Brands (Required)"
              required
            >
              {[{ name: 'Select All', value: 'selectAll' }, ...TRENDING_PLACES_BRANDS].map((brand) => (
                <MenuItem key={brand.value} value={brand.value}>
                  <Checkbox checked={brands.includes(brand.value)} />
                  <ListItemText primary={brand.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!imageId && (
            <Box display="flex" justifyContent="flex-start">
              <Button variant="outlined" component="label">
                Or upload a new image to assign
                <VisuallyHiddenInput onChange={(event) => onImageChange(event)} type="file" />
              </Button>
            </Box>
          )}

          <Box display="flex" alignItems="center" gap={2}>
            {imageId && (
              <Box>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  Image to assign
                </Typography>
                <Image options={{ width: 150, dpr: 3 }} publicId={imageId} loading="lazy" />
              </Box>
            )}
            {imageId && (
              <StyledButton sx={{ height: 10 }} onClick={() => setImageId(null)}>
                <CloseIcon />
              </StyledButton>
            )}
          </Box>
          {error}
          <Stack direction="column" gap={2}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!place || !imageId || !regions.length || !brands.length}
            >
              Assign image to selected markets and brands
            </Button>
            <Button variant="outlined" color="primary" onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </form>

      <Dialog open={successDialogOpen} onClose={handleSuccessDialogClose}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Alert severity="success">
            <strong>Image assigned for:</strong> {place?.format?.mainText}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default UpdateTrendingPlaceImageForm;
